/**
 * デバイスセンサー (compass / pitch / roll) ユーティリティ。
 *
 * Sprint BG-DataSettings 16 (GAP-U1 MUST、Sprint BF Part 1 の `useDeviceOrientation` を補完)。
 *
 * 旧 `old/apps/user/src/lib/sensors.ts` (110 行) を踏襲 (L2):
 *   - iOS Safari 13+ は `DeviceOrientationEvent.requestPermission()` が必要
 *   - Android / その他は 'deviceorientation' event を直接購読
 *   - 失敗 / unsupported / denied 時は silent fallback (null を返す、warning なし)
 *
 * `useDeviceOrientation` (既存、Sprint BF Part 1) との差分:
 *   - 既存 hook = `DeviceOrientationSample` (alpha/beta/gamma + capturedAt) を Firestore 書込み用に exposes
 *   - 本 module の `useOrientationSample()` = 同じ snapshot を React state として再 export する補助 hook
 *     (UI 側で compass 矢印描画等を将来追加する余地、Phase 6+ AR/3D 表示の foundation)
 *   - `requestOrientationPermission()` = iOS の `requestPermission()` を「'granted' | 'denied' | 'unsupported'」
 *     の 3 値で返す純粋関数 (旧と同シグネチャ、user gesture context で呼ぶ前提)
 *
 * docs/91 GAP-U1 (sensors MUST) close: 本 sprint で `useDeviceOrientation` hook の補完 API を提供。
 */
import type { DeviceOrientationSample } from '@citizen-walk/shared-types';
import { useEffect, useRef, useState } from 'react';

/** iOS 13+ の `DeviceOrientationEvent` constructor narrowing (TS lib に requestPermission が無いため拡張)。 */
interface DeviceOrientationEventConstructorIOS {
  requestPermission?: () => Promise<'granted' | 'denied'>;
}

interface DeviceOrientationEventIOS extends DeviceOrientationEvent {
  /** iOS Safari 専用: 真北基準 0-360°、最も信頼できる compass 値 (旧 v1 と同方針)。 */
  webkitCompassHeading?: number;
}

function isDeviceOrientationSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.DeviceOrientationEvent !== 'undefined';
}

function needsIosPermission(): boolean {
  if (!isDeviceOrientationSupported()) return false;
  const ctor = window.DeviceOrientationEvent as unknown as DeviceOrientationEventConstructorIOS;
  return typeof ctor.requestPermission === 'function';
}

/**
 * iOS 13+ の場合は `DeviceOrientationEvent.requestPermission()` を呼ぶ。
 * Android / その他は許可不要なので 'granted' を即返す。
 * unsupported (DeviceOrientationEvent 自体が無い) は 'unsupported'。
 *
 * iOS では「ユーザーのタップ直後の同期実行」内で呼ぶ必要がある (旧 v1 と同制約)。
 * throw した場合は silent に 'denied' を返す (UI に warning は出さない)。
 */
export async function requestOrientationPermission(): Promise<
  'granted' | 'denied' | 'unsupported'
> {
  if (!isDeviceOrientationSupported()) {
    return 'unsupported';
  }
  if (!needsIosPermission()) {
    return 'granted';
  }
  try {
    const ctor = window.DeviceOrientationEvent as unknown as DeviceOrientationEventConstructorIOS;
    const result = await ctor.requestPermission?.();
    return result === 'granted' ? 'granted' : 'denied';
  } catch {
    return 'denied';
  }
}

/** `GeolocationPositionError.code` の PERMISSION_DENIED 値 (W3C 仕様で固定の 1)。 */
const GEOLOCATION_PERMISSION_DENIED = 1;

/**
 * geolocation 権限の現在状態を Permissions API で取得する。
 *
 * `getCurrentPosition` は「許可済みでも」TIMEOUT / POSITION_UNAVAILABLE で reject しうるため、
 * 取得失敗を「許可拒否」と誤判定しないよう、許可そのものの状態を別経路で確認するのに使う。
 * Permissions API 非対応ブラウザ (一部 Safari 等) や query 失敗時は 'unknown' を返す。
 */
export async function getGeolocationPermissionState(): Promise<
  'granted' | 'denied' | 'prompt' | 'unknown'
> {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) {
    return 'unknown';
  }
  try {
    const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
    return status.state;
  } catch {
    return 'unknown';
  }
}

/**
 * 位置情報をリクエスト (許可ダイアログを表示する) 純粋 Promise ラッパ。
 *
 * old2 `apps/user/src/lib/sensors.ts` の `requestLocation` を踏襲 (onboarding の権限取得
 * フローで使用)。`useGeolocation` hook は state machine 版で imperative await できないため、
 * 「権限を許可してはじめる」ボタンの順次要求 (方位 → 位置) には本 Promise 版を使う。
 *
 * 取得堅牢化 (Android Chrome 対策): まず高精度 (GPS) で試み、PERMISSION_DENIED 以外の失敗
 * (TIMEOUT / POSITION_UNAVAILABLE) のときだけ低精度 (Wi-Fi/基地局) + 長め timeout + 直近キャッシュ
 * 許容で 1 回だけ再試行する。Android では `enableHighAccuracy:true` の GPS cold start が 10s では
 * 間に合わずタイムアウトしやすく、「許可済みなのに取得失敗」になるのを避けるため。
 * PERMISSION_DENIED (code 1) は再試行せず即 reject する (許可拒否は時間をかけても変わらない)。
 *
 * - geolocation 非対応端末では reject
 */
export function requestLocation(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(new Error('Geolocation API is not supported.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      resolve,
      (firstErr) => {
        if (firstErr.code === GEOLOCATION_PERMISSION_DENIED) {
          reject(firstErr);
          return;
        }
        // 高精度がタイムアウト等で失敗 → 低精度 + 直近 60s キャッシュ許容で再試行。
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 20000,
          maximumAge: 60000,
        });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
    );
  });
}

/**
 * compass / pitch / roll を `DeviceOrientationSample` 形式で読み出す純粋 hook。
 *
 * - undefined / permission denied / unsupported 時は null を返す (silent fallback)
 * - iOS では mount 時に自動で `requestPermission()` を呼ばない (user gesture が要るため)
 *   → 呼び出し側 (PostForm 等) が `requestOrientationPermission()` を user click 内で呼ぶ
 * - Android / その他は mount 時に即購読
 *
 * **Note**: `useDeviceOrientation` (既存 hook) との実装重複を避けるため、本 hook は
 * もっとシンプルな実装に留める (single subscription、cleanup あり、getCurrentOrientation は exposes しない)。
 */
export function useOrientationSample(): DeviceOrientationSample | null {
  const [sample, setSample] = useState<DeviceOrientationSample | null>(null);
  const attachedRef = useRef(false);

  useEffect(() => {
    if (!isDeviceOrientationSupported()) return;
    // iOS は user gesture 待ち (mount 時 attach しない、permission denied として null を返し続ける)
    if (needsIosPermission()) return;

    function handle(event: Event) {
      const ev = event as DeviceOrientationEventIOS;
      // iOS の webkitCompassHeading が真北基準 0-360° の補正済み値 (最も信頼できる)。
      // それが無い場合は alpha 生値を使う (Android では 360 - alpha が真北だが、
      // 本 hook は raw な alpha を保存する責務に留め、変換は UI 側で実施)。
      const alpha =
        typeof ev.webkitCompassHeading === 'number'
          ? ev.webkitCompassHeading
          : typeof ev.alpha === 'number'
            ? ev.alpha
            : null;
      const next: DeviceOrientationSample = {
        alpha,
        beta: typeof ev.beta === 'number' ? ev.beta : null,
        gamma: typeof ev.gamma === 'number' ? ev.gamma : null,
        capturedAt: new Date().toISOString(),
      };
      setSample(next);
    }

    window.addEventListener('deviceorientation', handle, true);
    attachedRef.current = true;
    return () => {
      if (attachedRef.current) {
        window.removeEventListener('deviceorientation', handle, true);
        attachedRef.current = false;
      }
    };
  }, []);

  return sample;
}
